// src/services/api.js
// ============================================================
// SIMPLIFIÉ AVEC TIMEOUTS : Empêche les freezes
// ============================================================
import { supabase } from '@/supabase/supabaseClient';
import axios from 'axios';
import { detectCountry } from '@/services/countries';

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';

// Inject X-Country header on every request so the backend can route to the
// correct Flash cloud function (FR vs IT) without touching each call site.
axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers['X-Country'] = detectCountry();
  return config;
});

// Cache simple pour la subscription uniquement
let _cachedSub = null;
let _fetchedAt = 0;

// Un seul rechargement auto par session tab pour ne pas boucler si l'échec persiste
const SUB_RELOAD_GUARD_KEY = 'qj_sub_fetch_reload_attempted';

// ============================================================
// HELPER : Promise avec timeout
// ============================================================
function withTimeout(promise, ms, errorMsg = 'Operation timeout') {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(errorMsg)), ms)
    )
  ]);
}

// ============================================================
// FONCTIONS DE SESSION AVEC TIMEOUT
// ============================================================

// Fallback synchrone : lit le token depuis localStorage (clé définie dans
// supabaseClient.js). Utilisé quand supabase.auth.getSession() dépasse le
// timeout de 3s — sans ça, les appels API partent avec `Bearer null` et se
// font rejeter en 401.
function readTokenFromStorage() {
  try {
    const raw = localStorage.getItem('sb-queryjuriste-auth');
    if (!raw) return null;
    const session = JSON.parse(raw);
    return session?.access_token || null;
  } catch (_) {
    return null;
  }
}

/**
 * Récupère le token d'accès Supabase avec timeout.
 */
export async function getSupabaseToken() {
  try {
    const { data, error } = await withTimeout(
      supabase.auth.getSession(),
      3000,
      'getSession timeout'
    );

    if (error || !data?.session) return readTokenFromStorage();
    return data.session.access_token;
  } catch (e) {
    return readTokenFromStorage();
  }
}

/**
 * Récupère la session Supabase complète avec timeout.
 */
export async function getSupabaseSession() {
  try {
    const { data, error } = await withTimeout(
      supabase.auth.getSession(),
      3000,
      'getSession timeout'
    );

    if (error) {
      return null;
    }
    return data?.session || null;
  } catch (e) {
    return null;
  }
}

/**
 * Fonction de compatibilité - ne fait plus rien car plus de cache custom.
 */
export function clearSessionCache() {
  _cachedSub = null;
  _fetchedAt = 0;
}

// ============================================================
// AXIOS INTERCEPTOR (Garde pour gérer les 401)
// ============================================================
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 (Unauthorized) and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Force Supabase to refresh the session
        const { data, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError) throw refreshError;

        if (data?.session) {
          // Update the header with new token
          const newToken = data.session.access_token;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

          // Retry the original request
          return axios(originalRequest);
        }
      } catch (refreshErr) {
        console.error("Session refresh failed:", refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================
// EXPORTED FUNCTIONS
// ============================================================

export async function createCheckoutSession(
  priceId,
  {
    firstName = '',
    lastName = '',
    phone = null,
    school = '',
    level = ''
  } = {}
) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const jwt = await getSupabaseToken();

  const resp = await axios.post(
    `${API_URL}/copilot/create-checkout-session`,
    {
      priceId,
      userId: user.id,
      email: user.email,
      firstName,
      lastName,
      phone,
      school,
      level
    },
    { headers: { Authorization: `Bearer ${jwt}` } }
  );
  return resp.data.url;
}

export async function getSubscription() {
  try {
    const token = await getSupabaseToken();
    if (!token) return { plan: "none", status: "none", queries_used: 0 };

    const resp = await withTimeout(
      axios.get(
        `${API_URL}/copilot/subscription-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      ),
      5000,
      'Subscription API timeout'
    );

    sessionStorage.removeItem(SUB_RELOAD_GUARD_KEY);
    return resp.data;
  } catch (e) {
    // Échec transitoire (token expiré, refresh trop lent, timeout...) : un
    // rechargement complet repart avec un client Supabase propre et un vrai
    // getSession(), ce qui résout la quasi-totalité des cas. Un seul essai
    // par session d'onglet pour ne pas boucler si l'échec est persistant
    // (vraie coupure réseau, backend down).
    if (!sessionStorage.getItem(SUB_RELOAD_GUARD_KEY)) {
      sessionStorage.setItem(SUB_RELOAD_GUARD_KEY, '1');
      window.location.reload();
    }
    return { plan: "none", status: "none", queries_used: 0 };
  }
}

// Cache simple pour la subscription (évite les appels répétés)
export async function getSubscriptionCached(maxAgeSec = 60) {
  const isFresh = Date.now() - _fetchedAt < maxAgeSec * 1000;
  if (_cachedSub && isFresh) {
    return _cachedSub;
  }

  _cachedSub = await getSubscription();
  _fetchedAt = Date.now();
  return _cachedSub;
}

// ============================================================
// RÔLES UTILISATEUR (is_admin, is_cabinet_admin, can_create_invites)
// ============================================================
// La table `user_roles` est en RLS sans policy de lecture : le client ne peut
// pas l'interroger directement, on passe donc par le backend.
// ⚠️ Ceci sert uniquement à masquer une UI que l'utilisateur n'a pas le droit
// d'utiliser. L'autorisation qui fait autorité est côté backend, sur chaque
// route admin (verify_admin_access).
let _cachedRoles = null;
let _rolesFetchedAt = 0;

const EMPTY_ROLES = {
  is_admin: false,
  is_cabinet_admin: false,
  can_create_invites: false
};

export async function getOwnRoles(maxAgeSec = 60) {
  if (_cachedRoles && Date.now() - _rolesFetchedAt < maxAgeSec * 1000) {
    return _cachedRoles;
  }

  const token = await getSupabaseToken();
  if (!token) return EMPTY_ROLES;

  const { data } = await withTimeout(
    axios.get(`${API_URL}/copilot/me/roles`, {
      headers: { Authorization: `Bearer ${token}` }
    }),
    10000,
    'Roles fetch timeout'
  );

  _cachedRoles = { ...EMPTY_ROLES, ...(data || {}) };
  _rolesFetchedAt = Date.now();
  return _cachedRoles;
}

export function invalidateRolesCache() {
  _cachedRoles = null;
  _rolesFetchedAt = 0;
}

// Alias pour compatibilité avec l'ancien code
export const getCachedSession = getSupabaseSession;

// ============================================================
// GESTION DES ACCES (console commerciale : Workspace + prolongation d'essai)
// Autorise aux comptes is_admin ou can_create_invites (verifie cote backend).
// ============================================================
async function _accessPost(path, body, timeoutMs = 30000) {
  const token = await getSupabaseToken();
  const { data } = await withTimeout(
    axios.post(`${API_URL}${path}`, body, {
      headers: { Authorization: `Bearer ${token}` }
    }),
    timeoutMs,
    'Access request timeout'
  );
  return data;
}

export function lookupAccess(query) {
  return _accessPost('/copilot/admin/access/lookup', { query });
}

export function setAccess(items) {
  return _accessPost('/copilot/admin/access/set', { items });
}

// ============================================================
// DEVIS (console commerciale : creation d'un devis Qonto en brouillon)
// Memes droits que la creation d'invitations (verifie cote backend).
// ============================================================
export function searchCompany(query) {
  return _accessPost('/copilot/admin/quotes/company-search', { query });
}

// ============================================================
// CLIENTS PAR VILLE (console commerciale : ice break avec les prospects)
// Memes droits que la creation d'invitations (verifie cote backend).
// ============================================================
export function getClientCities() {
  return _accessPost('/copilot/admin/client-cities', {});
}

export function previewQuote(form) {
  return _accessPost('/copilot/admin/quotes/preview', form);
}

export function createQuote(form) {
  return _accessPost('/copilot/admin/quotes/create', form);
}

export function updateQuote(quoteId, form) {
  return _accessPost('/copilot/admin/quotes/update', { ...form, quote_id: quoteId });
}

export function assistQuote(brief) {
  // L'assistant cherche sur le web : plusieurs minutes n'ont rien d'anormal.
  // Le backend repond en flux (battement de coeur toutes les 15 s), donc aucun
  // proxy ne coupe entre-temps et on peut attendre vraiment longtemps.
  return _accessPost('/copilot/admin/quotes/assist', { brief }, 660000);
}

export async function fetchQuotePdf(quoteId) {
  const token = await getSupabaseToken();
  const { data } = await withTimeout(
    axios.post(`${API_URL}/copilot/admin/quotes/pdf`, { quote_id: quoteId }, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    }),
    60000,
    'PDF timeout'
  );
  return data;
}

// ============================================================
// SIGNATURE ELECTRONIQUE DU DEVIS (Documenso auto-heberge)
// ============================================================
export function sendQuoteForSignature({ quoteId, signerEmail, signerName, withdrawalDays = 0 }) {
  return _accessPost('/copilot/admin/quotes/send-signature', {
    quote_id: quoteId, signer_email: signerEmail, signer_name: signerName,
    withdrawal_days: withdrawalDays
  });
}

// Suivi des devis envoyes en signature : chacun voit les siens, les admins tout.
export function listSignatures({ status = '', all = false } = {}) {
  return _accessPost('/copilot/admin/quotes/signature-list', { status, all });
}

export function remindSignature(documentId) {
  return _accessPost('/copilot/admin/quotes/remind', { document_id: documentId });
}

export function setReminders(documentId, enabled) {
  return _accessPost('/copilot/admin/quotes/reminders', {
    document_id: documentId, enabled
  });
}

export function cancelSignature(documentId, reason) {
  return _accessPost('/copilot/admin/quotes/cancel-signature', {
    document_id: documentId, reason
  });
}

// Tableau de bord des devis partis en signature (admin uniquement).
export async function quotesDashboard({ refresh = false } = {}) {
  const token = await getSupabaseToken();
  const { data } = await withTimeout(
    axios.get(`${API_URL}/copilot/admin/quotes/dashboard${refresh ? '?refresh=1' : ''}`, {
      headers: { Authorization: `Bearer ${token}` }
    }),
    // La reconstruction complete interroge Documenso, Qonto puis Stripe :
    // quelques secondes quand le cache serveur est froid.
    90000,
    'Dashboard devis timeout'
  );
  return data;
}

export function quoteSignatureStatus(documentId) {
  return _accessPost('/copilot/admin/quotes/signature-status', { document_id: documentId });
}
